import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { MarginTraderAccount } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { useSwyftxMargin } from 'src/lib/margin';
import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  title: string;
  dynamicColor?: boolean;
  dataKey: keyof Pick<
    MarginTraderAccount,
    'equity' | 'profitOpen' | 'balanceAvailable' | 'margin' | 'freeMargin' | 'balance'
  >;
  tooltip?: string;
  name?: string;
  size?: NumericProps['size'];
  className?: string;
};

const MarginAccountItem: React.FC<Props> = ({ title, size, dynamicColor, dataKey, tooltip, name, className }) => {
  const { account, currency } = useMarginAccount();
  const { isUnderMaintenance } = useSwyftxMargin();
  const { t } = useTranslation('margin');

  const hasValue = useMemo(() => !!account?.[dataKey], [account, dataKey]);

  return (
    <FlexLayout
      direction='column'
      justifyContent='space-between'
      className={cn('h-full gap-[6px] whitespace-nowrap', className)}
    >
      <EnhancedTooltip title={tooltip} name={name || title} align='start' side='top'>
        <span>
          <Body size='xsmall' color='secondary' underline='dashed'>
            {title}
          </Body>
        </span>
      </EnhancedTooltip>
      <Numeric
        weight='bold'
        size={size}
        color={dynamicColor && hasValue ? (Big(account?.profitOpen).gte(0) ? 'success' : 'error') : 'primary'}
      >
        {isUnderMaintenance && !hasValue
          ? t('maintenance.labels.unavailable')
          : formatCurrency(account?.[dataKey], currency, { appendCode: true, hideCode: false })}
      </Numeric>
    </FlexLayout>
  );
};

export { MarginAccountItem };
